import React from 'react';

import { Logger } from 'services/logger';
import { ONETRUST_CATEGORIES, OneTrustService } from 'services/oneTrust';

const sdkContent = require('!raw-loader!@contentstack/personalize-edge-sdk/dist/personalize-edge-sdk.min').default;

export const PERSONALIZE_TIMEOUT_CLASS = 'personalize-load-timeout';
const PERSONALIZE_TIMEOUT = 5000;

export class PersonalizeSDKError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PersonalizeSDKError';
  }
}

export class PersonalizeSDKService {
  static getPersonalizeInitializationScript() {
    return (
      <script
        key="personalize-init-script"
        dangerouslySetInnerHTML={{
          __html: `
      ${sdkContent}

      const TT_COOKIE_NAME = 'tt-user-id';

      const getTtUserId = () => {
        const match = document.cookie.split('; ').find(row => row.startsWith(TT_COOKIE_NAME + '='));
        return match ? decodeURIComponent(match.split('=')[1]) : null;
      };

      const setTtUserIdCookie = (userId) => {
        // Only production shares the cookie across the root domain; other envs (e.g. staging.tastytrade.com) stay isolated.
        const rootDomain = '${process.env.GATSBY_ENV}' === 'production'
          ? location.hostname.split('.').slice(-2).join('.')
          : location.hostname;
        const maxAge = 31536000;
        document.cookie = TT_COOKIE_NAME + '=' + encodeURIComponent(userId) + '; Domain=.' + rootDomain + '; Path=/; Max-Age=' + maxAge + '; SameSite=Lax';
      };

      const earlyFetchPersonalizeData = async (sdk) => {
        const uid = window.__pageUid;
        const includeQuery = window.__pageIncludes;
        if (!uid || !includeQuery) return;

        const aliases = sdk.getVariantAliases();
        if (!aliases || aliases.length === 0) return;

        const cdnHost = '${process.env.CONTENTSTACK_CDN_HOST || ''}';
        const apiKey = '${process.env.CONTENTSTACK_API_KEY || ''}';
        const accessToken = '${process.env.CONTENTSTACK_DELIVERY_TOKEN || ''}';
        const environment = '${process.env.CONTENTSTACK_ENVIRONMENT || ''}';
        if (!cdnHost || !apiKey || !accessToken || !environment) return;

        const url = 'https://' + cdnHost + '/v3/content_types/_page_page/entries/' + uid + '?' + includeQuery + '&environment=' + environment;

        window.__personalizePagePromise = fetch(url, {
          headers: {
            api_key: apiKey,
            access_token: accessToken,
            'x-cs-variant-uid': aliases.join(','),
          },
        })
          .then(function(r) { return r.ok ? r.json() : null; })
          .then(function(d) { return d?.entry?.publish_details?.variants ? d.entry : null; })
          .catch(function() { return null; });
      };

      const initPersonalizeSDK = async () => {
        if (window.personalizeSDK) {
          return window.personalizeSDK;
        }

        window.personalizeSDK = (async () => {
          const existingUserId = getTtUserId();
          const initOptions = existingUserId ? { userId: existingUserId } : {};
          const sdk = await personalize.init('${process.env.CONTENTSTACK_PERSONALIZE_UID || ''}', initOptions);

          if (!existingUserId) {
            const generatedUserId = sdk.getUserId();
            if (generatedUserId) {
              setTtUserIdCookie(generatedUserId);
            }
          }

          earlyFetchPersonalizeData(sdk);

          return sdk;
        })();

        return window.personalizeSDK;
      };

      initPersonalizeSDK();
    `,
        }}
      />
    );
  }

  static getPersonalizeTimeoutScript() {
    return (
      <script
        key="personalize-timeout-script"
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `
      window.personalizeTimer = setTimeout(() => {
        window.personalizeLoadTimeout = true;
        document.body.classList.add('${PERSONALIZE_TIMEOUT_CLASS}');
      }, ${PERSONALIZE_TIMEOUT});
      `,
        }}
      />
    );
  }

  /*
   * Personalize SDK is loaded in html <head> to give it the highest priority.
   * Communication happens through global objects.
   */
  static async waitForSdk() {
    if (!window.personalizeSDK) {
      throw new PersonalizeSDKError('Personalize SDK not initialized.');
    }
    const sdk = await window.personalizeSDK;

    if (PersonalizeSDKService.hasTimeout()) {
      throw new PersonalizeSDKError('Personalize SDK load timeout exceeded.');
    }

    if (!sdk || typeof sdk !== 'object') {
      throw new PersonalizeSDKError('Personalize SDK is not available.');
    }

    return sdk;
  }

  /**
   * Check if timeout for loading personalized content has been reached
   * @returns {boolean}
   */
  static hasTimeout() {
    return !!window.personalizeLoadTimeout;
  }

  /**
   * Clears global timer that loads Personalize SDK only within a certain time frame.
   */
  static clearTimer() {
    clearTimeout(window.personalizeTimer);
  }

  static async setAttribute(attribute, value) {
    try {
      const targetingConsent = await OneTrustService.checkConsent(ONETRUST_CATEGORIES.targeting);
      if (!targetingConsent) {
        Logger.warn('Targeting consent not granted, skipping Personalize attribute setting.');
        return;
      }

      const personalize = await PersonalizeSDKService.waitForSdk();
      if (!personalize) return;
      await personalize.set({
        [attribute]: value,
      });
    } catch (error) {
      Logger.warn('Error setting visitor status in Personalize SDK:', error);
    }
  }
}
